import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import type { JSX, ReactNode } from "react";
import { useLocation, useParams } from "react-router-dom";

import { BackButton } from "../../components/ui/back-button.js";
import { NoAccessPanel } from "../../components/ui/no-access-panel.js";
import { PageLoader } from "../../components/ui/page-loader.js";
import { isPermissionDenied } from "../../lib/api.js";
import { fetchDocFileBytes, fetchDocSourceFileBytes, getDoc } from "./api.js";
import { downloadDocument } from "./downloadDocument.js";
import { type DocReaderMode, selectDocReaderMode } from "./docReader.js";
import { FaqReader } from "./FaqReader.js";
import { Markdown } from "./markdown.js";
import { PdfInlinePreview } from "./PdfInlinePreview.js";
import type { DocDetail } from "./types.js";
import { KB_PATH, kbFolderPath } from "./kb-paths.js";
import { TRAININGS_PATH } from "../portal/trainings/training-paths.js";
import { formatBytes } from "./utils.js";
import { VideoReader } from "./VideoReader.js";

/** Where a Back button goes, and what it says. */
export interface BackTarget {
  to: string;
  label: string;
}

/**
 * Where Back goes from an article, and what it says. A TRAINING goes back to
 * Trainings, the one page that lists it (2026-09-17); a library article goes
 * back to its FOLDER; release documentation (a pinned document, reached from
 * the release page) goes back to Releases, because that is where it is
 * browsed; an article filed nowhere goes to Documentation itself.
 *
 * The page that LINKED here can say better — see `readBackState`.
 */
export function articleBackTarget(
  d: Pick<DocDetail, "category" | "default_release" | "purpose">,
): BackTarget {
  if (d.purpose === "training") return { to: TRAININGS_PATH, label: "Online Training" };
  if (d.default_release) return { to: "/releases", label: "Software Releases" };
  if (d.category) return { to: kbFolderPath(d.category.slug), label: d.category.name };
  return { to: KB_PATH, label: "Documentation" };
}

/**
 * A Back target handed over in router STATE by the page that linked here —
 * Documentation ▸ Release documents passes `{ back: { to, label } }` so its
 * rows return to the release version they were opened from rather than to
 * the Releases page. Only an in-app path is honoured; anything else is
 * ignored and the article's own target applies.
 */
export function readBackState(state: unknown): BackTarget | null {
  if (!state || typeof state !== "object" || !("back" in state)) return null;
  const back = (state as { back: unknown }).back;
  if (!back || typeof back !== "object") return null;
  const { to, label } = back as { to?: unknown; label?: unknown };
  if (typeof to !== "string" || typeof label !== "string") return null;
  if (!to.startsWith("/") || to.startsWith("//")) return null;
  return { to, label };
}

/**
 * The article reader: a compact header (identity + Download) over a
 * TYPE-AWARE body (`DocReaderBody` / `selectDocReaderMode`) — FAQ → Q&A,
 * video → player, an authored body → the Markdown renderer, an uploaded PDF
 * → the inline PDF reader, images and audio inline.
 *
 * Ported from the command center's reader without its Edit action and
 * without the Share menu. Sharing a document onward is a real thing a
 * partner might want, but it is a decision about what leaves XOP's hands
 * and it has not been made — the Download button is the sanctioned way for
 * a reseller to take a datasheet to their own customer.
 */
export function ArticlePage({
  back: routeBack,
}: {
  /** Set by a route that is a section of its own — `/trainings/:slug` passes
   * Trainings — so Back (and the not-found fallback) returns there whatever
   * the document's own filing says. */
  back?: BackTarget;
} = {}): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const stateBack = readBackState(location.state);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["docs", "single", slug],
    queryFn: () => getDoc(slug ?? ""),
    enabled: !!slug,
  });

  if (isLoading) return <PageLoader />;

  if (isError || !data) {
    if (isPermissionDenied(error)) {
      return <NoAccessPanel permission={error.permission ?? "documentation:view"} />;
    }
    return (
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 14, color: "error.main" }}>
            Could not load document:{" "}
            {error instanceof Error ? error.message : "not found"}
          </Typography>
          <BackButton
            to={routeBack?.to ?? KB_PATH}
            label={`Back to ${routeBack?.label ?? "Documentation"}`}
            sx={{ mt: 1.5 }}
          />
        </CardContent>
      </Card>
    );
  }

  const d = data.data;
  const file = d.files[0];
  // The faithful original PDF (whitepaper / datasheet / press release) parked
  // in its own association — it coexists with the markdown body. When present
  // it is both the inline-preview source AND what Download saves.
  const sourceFile = d.source_file ?? null;

  // ONE "Download" action for the whole reader — the same helper the list
  // rows use, so both save the same file (see downloadDocument.ts).
  async function downloadDoc(): Promise<void> {
    setDownloadBusy(true);
    setDownloadError(null);
    try {
      await downloadDocument(d);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Could not download this document");
    } finally {
      setDownloadBusy(false);
    }
  }

  const updated = formatDateMaybe(d.updated_at);
  const back = routeBack ?? stateBack ?? articleBackTarget(d);

  // A dot-separated meta line under the title. The filename is its own
  // small, secondary node rather than a second heading — as a bold line it
  // read as "the title, shown twice".
  const meta: ReactNode[] = [];
  if (file) {
    meta.push(
      <Box component="span" key="filename" sx={{ fontWeight: 500, color: "text.secondary" }}>
        {file.filename}
      </Box>,
    );
    meta.push(<span key="size">{formatBytes(file.byte_size)}</span>);
  }
  if (sourceFile) {
    meta.push(
      <Box component="span" key="original" sx={{ color: "text.secondary" }}>
        Original PDF · {formatBytes(sourceFile.byte_size)}
      </Box>,
    );
  }
  if (updated) meta.push(<span key="updated">Updated {updated}</span>);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {downloadError && (
        <Typography sx={{ fontSize: 12, color: "error.main" }} role="alert">
          {downloadError}
        </Typography>
      )}

      <Card>
        <CardContent sx={{ display: "grid", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, display: "grid", gap: 1 }}>
              <BackButton to={back.to} label={back.label} sx={{ justifySelf: "start" }} />
              <Typography
                component="h1"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                }}
              >
                {d.title}
              </Typography>

              {/* No status chip. Every document reachable here is
                  published — the command center shows Draft / In review
                  because staff can see those, and a chip that always reads
                  the same is decoration. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                {d.category && <DocChip tone="brand">{d.category.name}</DocChip>}
                {d.default_release && <DocChip tone="neutral">v{d.default_release.version}</DocChip>}
                {d.featured && <DocChip tone="warn">Featured</DocChip>}
                {d.solution_areas.map((a) => (
                  <DocChip key={a.id} tone="neutral">
                    {a.name}
                  </DocChip>
                ))}
              </Box>

              {meta.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: 12,
                    color: "text.disabled",
                  }}
                >
                  {meta.map((node, idx) => (
                    <Fragment key={idx}>
                      {idx > 0 && (
                        <Box component="span" aria-hidden sx={{ color: "text.disabled" }}>
                          ·
                        </Box>
                      )}
                      {node}
                    </Fragment>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexShrink: 0,
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                data-testid="doc-download"
                onClick={() => void downloadDoc()}
                disabled={downloadBusy}
                sx={{ bgcolor: "action.hover" }}
              >
                {downloadBusy ? "Preparing…" : "Download"}
              </Button>
            </Box>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <DocReaderBody detail={d} />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

/** ISO → locale date, or null for empty/invalid input (the serializer sends
 * "" for a never-updated doc, which would otherwise read "Invalid Date"). */
function formatDateMaybe(value: string | null | undefined): string | null {
  if (!value) return null;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt.toLocaleDateString();
}

/** Taxonomy pill in the doc header. */
function DocChip({
  tone,
  children,
}: {
  tone: "neutral" | "good" | "warn" | "brand";
  children: ReactNode;
}): JSX.Element {
  const toneSx: SxProps<Theme> =
    tone === "good"
      ? { bgcolor: (t) => alpha(t.palette.success.main, 0.15), color: "success.main" }
      : tone === "warn"
        ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.15), color: "warning.main" }
        : tone === "brand"
          ? { bgcolor: "action.selected", color: "primary.main" }
          : { bgcolor: "surfaceSoft", color: "text.secondary" };

  return (
    <Chip
      label={children}
      size="small"
      sx={[{ height: 22, borderRadius: 0.75, fontSize: 12, "& .MuiChip-label": { px: 1 } }, toneSx]}
    />
  );
}

/**
 * Type-aware reader body. Picks the renderer from `selectDocReaderMode`
 * (pure, unit-tested in `docReader.test.ts`) so the reader NEVER dumps a raw
 * markdown body as plain text:
 *
 *   • video      → `<VideoReader>` (upload `<video>` or hosted iframe)
 *   • faq        → `<FaqReader>` (one Q&A per item)
 *   • markdown   → the authored body, through the Markdown renderer
 *   • pdf        → `<PdfInlinePreview>` fed by the `source_file` ORIGINAL
 *                  or the uploaded PDF
 *   • text       → an uploaded text file, as markdown or preformatted
 *   • image/av   → native `<img>` / `<video>` / `<audio>`
 *   • file-card  → a "no inline preview" card for un-renderable binaries
 *   • empty      → an empty-state note
 */
function DocReaderBody({ detail }: { detail: DocDetail }): JSX.Element {
  const mode = selectDocReaderMode(detail);
  switch (mode.kind) {
    case "video":
      return <VideoReader detail={detail} />;
    case "faq":
      return <FaqReader detail={detail} />;
    case "markdown":
      // Capped line length, the same as the uploaded-markdown reader below.
      return (
        <Box data-testid="doc-markdown" sx={{ maxWidth: "76ch" }}>
          <Markdown source={detail.content ?? ""} />
        </Box>
      );
    case "pdf": {
      const props = pdfReaderProps(detail, mode);
      return (
        <PdfInlinePreview
          docId={detail.id}
          filename={props.filename}
          loader={props.loader}
          cacheKey={props.cacheKey}
        />
      );
    }
    case "text":
      return <TextFileReader docId={detail.id} markdown={mode.markdown} />;
    case "image":
    case "av":
    case "file-card":
      return <FilePreview mode={mode} />;
    case "empty":
    default:
      return (
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          This document has no content yet.
        </Typography>
      );
  }
}

/** Byte source + cache key + filename for the two PDF reader modes:
 *   • `source` → the faithful ORIGINAL PDF via the `/source-file` relay
 *   • `file`   → the uploaded PDF via the `/file` relay
 * Both same-origin, so nothing cross-origin is fetched by the page. */
function pdfReaderProps(
  detail: DocDetail,
  mode: Extract<DocReaderMode, { kind: "pdf" }>,
): {
  loader: () => Promise<{ bytes: ArrayBuffer; contentType: string | null }>;
  cacheKey: readonly unknown[];
  filename: string;
} {
  const fallbackName = `${detail.slug}.pdf`;
  if (mode.via === "source") {
    return {
      loader: () => fetchDocSourceFileBytes(detail.id),
      cacheKey: ["docs", "pdf", "source", detail.id],
      filename: mode.file?.filename ?? fallbackName,
    };
  }
  return {
    loader: () => fetchDocFileBytes(detail.id),
    cacheKey: ["docs", "pdf", "file", detail.id],
    filename: mode.file?.filename ?? fallbackName,
  };
}

/**
 * Inline reader for an uploaded TEXT file. Markdown (`.md`) renders through
 * the same `<Markdown>` renderer the authored body uses, so an uploaded `.md`
 * reads exactly like a hand-written article; any other plain-text file
 * (`.txt`, `.csv`, `.json`, `.log`, …) renders preformatted. Bytes arrive
 * through the same-origin relay and are decoded as UTF-8. On any failure it
 * degrades to a "use Download" card, never a blank.
 */
function TextFileReader({ docId, markdown }: { docId: number; markdown: boolean }): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["docs", "text", docId],
    queryFn: async () => {
      const { bytes } = await fetchDocFileBytes(docId);
      return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
    },
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (isError || data == null) {
    return (
      <Box
        data-testid="doc-text-error"
        sx={{
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
          p: 2,
          fontSize: 14,
          color: "text.disabled",
        }}
      >
        Couldn’t load this file inline. Use{" "}
        <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
          Download
        </Box>{" "}
        above to save the original.
      </Box>
    );
  }

  if (markdown) {
    // Cap the line length for readability, exactly like the authored body.
    return (
      <Box data-testid="doc-text-markdown" sx={{ maxWidth: "76ch" }}>
        <Markdown source={data} />
      </Box>
    );
  }

  return (
    <Box
      component="pre"
      data-testid="doc-text-plain"
      sx={{
        m: 0,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 13,
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowX: "auto",
      }}
    >
      {data}
    </Box>
  );
}

/**
 * Native inline preview for an uploaded IMAGE / AUDIO / VIDEO file, or a
 * "no inline preview" card for an un-renderable binary (Office, zip, …).
 * PDFs never reach here — they go through `<PdfInlinePreview>` upstream.
 * `url` resolves to `preview_url ?? download_url`; an empty one means the
 * upload never finished, which is worth saying rather than showing a blank.
 */
function FilePreview({
  mode,
}: {
  mode: Extract<DocReaderMode, { kind: "image" | "av" | "file-card" }>;
}): JSX.Element {
  const file = mode.file;
  const url = file.preview_url ?? file.download_url ?? "";

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {!url && mode.kind !== "file-card" && (
        <Typography sx={{ fontSize: 14, color: "error.main" }}>
          The file URL is missing — the upload may not have finished. Ask your
          XOP Networks contact to re-upload it.
        </Typography>
      )}

      {url && mode.kind === "image" && (
        <Box
          sx={{
            overflow: "hidden",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: (t) => t.shadows[1],
          }}
        >
          {/* alt intentionally mirrors the filename for this preview. */}
          <Box
            component="img"
            src={url}
            alt={file.filename}
            sx={{ display: "block", maxHeight: "70vh", width: "100%", objectFit: "contain" }}
          />
        </Box>
      )}

      {url && mode.kind === "av" && mode.media === "video" && (
        <Box
          component="video"
          controls
          preload="metadata"
          sx={{ width: "100%", borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "#000" }}
        >
          <source src={url} type={file.content_type ?? undefined} />
          Your browser does not support inline video playback.{" "}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open the file directly.
          </a>
        </Box>
      )}

      {url && mode.kind === "av" && mode.media === "audio" && (
        <Box component="audio" controls preload="metadata" sx={{ width: "100%" }}>
          <source src={url} type={file.content_type ?? undefined} />
          Your browser does not support inline audio playback.{" "}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open the file directly.
          </a>
        </Box>
      )}

      {mode.kind === "file-card" && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
            p: 2,
            fontSize: 14,
            color: "text.disabled",
          }}
        >
          This file type has no inline preview. Use{" "}
          <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
            Download
          </Box>{" "}
          above to save the original file locally.
        </Box>
      )}
    </Box>
  );
}
